
import React, { useState, useMemo } from 'react';
import type { Recipe, Role, User, Language, Ingredient } from '../types';
import HamIcon from './icons/HamIcon';
import DrumstickIcon from './icons/DrumstickIcon';
import FishIcon from './icons/FishIcon';
import LeafIcon from './icons/LeafIcon';
import UtensilsCrossedIcon from './icons/UtensilsCrossedIcon';
import ClockIcon from './icons/ClockIcon';
import SnowflakeIcon from './icons/SnowflakeIcon';
import PencilIcon from './icons/PencilIcon';
import DragHandleIcon from './icons/DragHandleIcon';
import UsersIcon from './icons/UsersIcon';
import CalendarPlusIcon from './icons/CalendarPlusIcon';
import StarIcon from './icons/StarIcon';
import HeartIcon from './icons/HeartIcon';
import RecipeOwnerInfoModal from './RecipeOwnerInfoModal';
import { getTranslation } from '../utils/translations';
import { isRecipeSavePending } from '../utils/recipeSaveStatus';
import { getRecipeHeaderImageUrl } from '../utils/recipeMedia';
import SparklesIcon from './icons/SparklesIcon';

interface RecipeCardProps {
    recipe: Recipe;
    role: Role;
    canManage: boolean;
    onEdit: () => void;
    onView?: () => void; 
    isReorderMode: boolean;
    onReorder: (draggedRecipeId: string, targetRecipeId: string) => void;
    isSelectionMode: boolean;
    isSelected: boolean;
    onToggleSelect: (id: string) => void;
    currentUserId: string;
    showPrivateBadge: boolean;
    onSchedule: () => void;
    onDuplicate?: () => void;
    scheduleSummary?: string[];
    allUsers?: User[];
    activeHouseholdId?: string;
    language?: Language;
    isMobile?: boolean;
    isDefrostDay?: boolean;
    layout?: 'grid' | 'list';
    isFavorite?: boolean;
    onToggleFavorite?: () => void;
    pickerMode?: boolean;
}

const SelectionCheckbox = ({ checked }: { checked: boolean }) => (
    <div className={`
        w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ease-out
        ${checked ? 'bg-brand-sage border-brand-sage shadow-md scale-110' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 shadow-sm'}
    `}>
        {checked && (
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
        )}
    </div>
);

const RecipeCard: React.FC<RecipeCardProps> = ({ 
    recipe, 
    role, 
    canManage,
    onEdit, 
    onView,
    isReorderMode, 
    onReorder,
    isSelectionMode,
    isSelected,
    onToggleSelect,
    currentUserId,
    showPrivateBadge,
    onSchedule,
    onDuplicate,
    allUsers,
    activeHouseholdId,
    language,
    isMobile = false,
    isDefrostDay = false,
    layout = 'grid',
    isFavorite = false,
    onToggleFavorite,
    pickerMode = false,
}) => {
    const [showOwnerInfo, setShowOwnerInfo] = useState(false);
    const t = (key: any) => getTranslation(language, key);
    const isPendingSave = isRecipeSavePending(recipe);
    const headerImageSrc = getRecipeHeaderImageUrl(recipe);
    
    const userLang = language || 'en';
    const sourceLang = recipe.originLanguage || 'en';
    const showOriginal = userLang === sourceLang;
    
    const displayName = showOriginal 
        ? recipe.name 
        : (recipe.englishTranslation?.name || recipe.name);

    // Dietary Logic
    const { meatWeight, isFish } = useMemo(() => {
        let weight = 0;
        let fish = false;
        const fishKeywords = ['fish', 'salmon', 'tuna', 'cod', 'shrimp', 'sea', 'דג', 'סלמון', 'טונה'];
        
        const nameLower = recipe.name.toLowerCase();
        if (fishKeywords.some(k => nameLower.includes(k))) fish = true;

        recipe.ingredients.forEach(ing => {
            if (ing.isMeat) {
                let qty = ing.quantity || 0;
                if (ing.unit?.toLowerCase() === 'kg' || ing.unit === 'ק"ג') qty *= 1000;
                weight += qty;
                
                const ingNameLower = ing.name.toLowerCase();
                if (fishKeywords.some(k => ingNameLower.includes(k))) fish = true;
            }
        });
        return { meatWeight: weight, isFish: fish };
    }, [recipe.ingredients, recipe.name]);

    const owner = useMemo(() => allUsers?.find(u => u.uid === recipe.ownerId), [allUsers, recipe.ownerId]);

    const isHeavyMeat = meatWeight > 800;

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        if (!canManage || isSelectionMode || isMobile || isPendingSave) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData("application/recipe-id", recipe.id);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleCardClick = () => {
        if (isPendingSave) return;
        if (pickerMode && onView) {
            onView();
            return;
        }
        if (isSelectionMode) {
            onToggleSelect(recipe.id);
        } else if (onView) {
            onView();
        }
    };

    const favoriteButton = onToggleFavorite ? (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            className={`p-1.5 rounded-full transition-all ${isFavorite ? 'text-rose-500 hover:text-rose-600' : 'text-slate-300 dark:text-slate-600 hover:text-rose-400'}`}
            title={isFavorite ? t('unfavorite') : t('favorite')}
        >
            <HeartIcon className="w-4 h-4" filled={isFavorite} />
        </button>
    ) : null;

    // Shared Icons & Color Logic
    let HeroIcon = UtensilsCrossedIcon;
    let iconBgClass = "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500";
    let accentColorClass = "bg-slate-200 dark:bg-slate-800";

    if (isHeavyMeat) {
        HeroIcon = HamIcon;
        iconBgClass = "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400";
        accentColorClass = "bg-gradient-to-r from-meat-gradient-start to-meat-gradient-end";
    } else if (isFish) {
        HeroIcon = FishIcon;
        iconBgClass = "bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-400";
        accentColorClass = "bg-sky-400 dark:bg-sky-700";
    } else if (meatWeight > 0) {
        HeroIcon = DrumstickIcon;
        iconBgClass = "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400";
        accentColorClass = "bg-brand-orange";
    } else if (recipe.isKeto) {
        HeroIcon = LeafIcon;
        iconBgClass = "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400";
        accentColorClass = "bg-brand-sage";
    }

    if (layout === 'list') {
        return (
            <div
                onClick={handleCardClick}
                aria-disabled={isPendingSave}
                title={isPendingSave ? t('recipeSavePending') : undefined}
                className={`
                    flex items-center p-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 gap-4 transition-all duration-200 group relative
                    ${isSelected ? 'bg-sky-50 dark:bg-sky-900/20' : ''}
                    ${isHeavyMeat && !isSelected ? 'border-l-4 border-l-rose-500' : ''}
                    ${isPendingSave
                        ? 'opacity-60 cursor-not-allowed'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/80 cursor-pointer'}
                    select-none
                `}
                style={{ touchAction: 'pan-y' }}
            >
                {!pickerMode && !isSelectionMode && canManage && !isMobile && !isPendingSave && (
                    <div 
                        draggable
                        onDragStart={handleDragStart}
                        className="p-1 text-slate-200 dark:text-slate-700 hover:text-slate-400 dark:hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DragHandleIcon className="w-5 h-5" />
                    </div>
                )}

                <div className="flex-shrink-0 w-10 flex justify-center">
                    {isSelectionMode && !isPendingSave ? (
                        <div className="animate-scale-in">
                            <SelectionCheckbox checked={isSelected} />
                        </div>
                    ) : isPendingSave ? (
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-50 dark:bg-amber-900/30 text-brand-orange">
                            <SparklesIcon className="w-5 h-5 animate-pulse" />
                        </div>
                    ) : headerImageSrc ? (
                        <img
                            src={headerImageSrc}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-slate-100 dark:border-slate-700"
                        />
                    ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBgClass}`}>
                            <HeroIcon className="w-6 h-6" />
                        </div>
                    )}
                </div>

                <div className="flex-grow min-w-0">
                    <div className="flex items-start gap-2">
                        <h4 className="font-sans font-bold text-sm text-slate-700 dark:text-slate-200 line-clamp-2 break-words whitespace-normal leading-snug flex-grow">
                            {displayName}
                            {recipe.isKeto && (
                                <span className="inline-block text-[8px] font-black text-green-600 dark:text-green-400 uppercase tracking-tighter bg-green-50 dark:bg-green-900/30 px-1 rounded ltr:ml-1.5 rtl:mr-1.5 align-middle">
                                    {t('ketoBadge')}
                                </span>
                            )}
                            {recipe.isTemporary && (
                                <span className="inline-block text-[8px] font-black text-amber-700 dark:text-amber-300 uppercase tracking-tighter bg-amber-50 dark:bg-amber-900/40 px-1 rounded ltr:ml-1.5 rtl:mr-1.5 align-middle">
                                    {t('temporaryRecipeBadge')}
                                </span>
                            )}
                        </h4>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 dark:text-slate-500 font-bold mt-0.5">
                        {isPendingSave ? (
                            <span className="text-brand-orange animate-pulse">{t('recipeSavePending')}</span>
                        ) : (
                            <>
                                <span className="flex items-center gap-0.5">
                                    <ClockIcon className="w-2.5 h-2.5" />
                                    {recipe.prepTime || 20}{t('card_mins')}
                                </span>
                                <span>•</span>
                                <span className="flex items-center gap-0.5">
                                    <UsersIcon className="w-2.5 h-2.5" />
                                    {recipe.servings}{t('card_servings')}
                                </span>
                                {recipe.averageRating !== undefined && recipe.averageRating > 0 && (
                                    <>
                                        <span>•</span>
                                        <span className="flex items-center gap-0.5 text-amber-500">
                                            {recipe.averageRating.toFixed(1)}
                                            <StarIcon className="w-2.5 h-2.5" filled />
                                        </span>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {!pickerMode && !isSelectionMode && !isPendingSave && (
                    <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        {favoriteButton}
                        <button onClick={(e) => { e.stopPropagation(); onSchedule(); }} className="p-2 text-slate-300 dark:text-slate-600 hover:text-brand-sage dark:hover:text-brand-sage">
                            <CalendarPlusIcon className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Default Grid Card Layout
    return (
        <>
            <div
                onClick={handleCardClick}
                aria-disabled={isPendingSave}
                title={isPendingSave ? t('recipeSavePending') : undefined}
                className={`
                    bg-white dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden text-right transition-all duration-200 border border-slate-100 dark:border-slate-800
                    ${isSelected ? 'ring-2 ring-brand-sage shadow-md transform scale-[1.02]' : ''}
                    ${isPendingSave
                        ? 'opacity-60 cursor-not-allowed'
                        : 'cursor-pointer hover:shadow-md'}
                    select-none
                    flex flex-col relative h-full group
                `}
                style={{ touchAction: 'pan-y' }}
            >
                <div className={`${headerImageSrc && !isPendingSave ? 'h-28' : 'h-12'} w-full flex items-center justify-center transition-colors relative overflow-hidden ${isPendingSave ? 'bg-gradient-to-r from-amber-300 to-brand-orange' : headerImageSrc ? 'bg-slate-100 dark:bg-slate-800' : accentColorClass}`}>
                    {!isSelectionMode && canManage && !isMobile && !isPendingSave && (
                        <div 
                            draggable
                            onDragStart={handleDragStart}
                            className="absolute top-1 left-8 rtl:right-8 rtl:left-auto p-1 text-white/40 hover:text-white/80 cursor-grab active:cursor-grabbing rounded-md hover:bg-black/10 transition-all z-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <DragHandleIcon className="w-4 h-4" />
                        </div>
                    )}

                    {isPendingSave ? (
                        <SparklesIcon className="w-5 h-5 text-white opacity-90 animate-pulse" />
                    ) : headerImageSrc ? (
                        <img src={headerImageSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                        <HeroIcon className="w-5 h-5 text-white opacity-90" />
                    )}
                    
                    {isDefrostDay && !isSelectionMode && !isPendingSave && (
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-1 rounded-full shadow-sm">
                            <SnowflakeIcon className="w-3 h-3 text-sky-500" />
                        </div>
                    )}

                    {!isSelectionMode && !isPendingSave && (
                        <div className="absolute top-2 left-2 rtl:right-2 rtl:left-auto z-20">
                            {favoriteButton}
                        </div>
                    )}

                    {!isSelectionMode && canManage && !isPendingSave && (
                        <div className="absolute top-2 right-2 rtl:left-2 rtl:right-auto z-20">
                            <button 
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onSchedule(); }} 
                                className="p-1.5 rounded-full transition-all text-slate-300 dark:text-slate-600 hover:text-brand-sage dark:hover:text-brand-sage"
                                title={t('schedule')}
                            >
                                <CalendarPlusIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {isPendingSave && (
                        <div className="absolute top-2 right-2 rtl:left-2 rtl:right-auto z-20">
                            <span className="text-[8px] font-black uppercase tracking-widest bg-white/95 text-brand-orange px-2 py-0.5 rounded-full shadow-sm animate-pulse">
                                {t('recipeSavePending')}
                            </span>
                        </div>
                    )}

                    {isSelectionMode && !isPendingSave && (
                        <div className="absolute top-2 left-2 rtl:right-2 rtl:left-auto z-20 animate-fade-in">
                            <SelectionCheckbox checked={isSelected} />
                        </div>
                    )}
                </div>

                <div className="p-3 flex flex-col flex-grow gap-2">
                    <div className="flex justify-between items-start gap-1">
                        <h4 className="font-sans font-bold text-xs sm:text-sm leading-tight text-slate-800 dark:text-slate-200 line-clamp-2 flex-grow min-h-[2.5rem]">
                            {displayName}
                            {recipe.isTemporary && (
                                <span className="inline-block text-[8px] font-black text-amber-700 dark:text-amber-300 uppercase tracking-tighter bg-amber-50 dark:bg-amber-900/40 px-1 rounded ltr:ml-1.5 rtl:mr-1.5 align-middle">
                                    {t('temporaryRecipeBadge')}
                                </span>
                            )}
                        </h4>
                    </div>

                    <div className="mt-auto pt-2 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between">
                        {isPendingSave ? (
                            <span className="text-[10px] font-black text-brand-orange uppercase tracking-widest animate-pulse">
                                {t('recipeSavePending')}
                            </span>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <div className="flex items-center gap-0.5 text-[10px] text-slate-400 dark:text-slate-500 font-bold whitespace-nowrap uppercase tracking-widest">
                                        <ClockIcon className="w-2.5 h-2.5" />
                                        <span>{recipe.prepTime || 20}{t('card_mins')}</span>
                                    </div>
                                    {recipe.isKeto && (
                                        <span className="text-[9px] font-black text-green-600 dark:text-green-400 uppercase tracking-tighter bg-green-50 dark:bg-green-900/30 px-1 rounded">{t('ketoBadge')}</span>
                                    )}
                                </div>
                        
                                {recipe.averageRating !== undefined && recipe.averageRating > 0 ? (
                                    <div className="flex items-center gap-0.5 text-amber-500 font-black text-[10px]">
                                        <span>{recipe.averageRating.toFixed(1)}</span>
                                        <StarIcon className="w-2.5 h-2.5" filled />
                                    </div>
                                ) : (
                                    <span className="text-[10px] text-slate-300 dark:text-slate-600 font-black uppercase">{recipe.servings}{t('card_servings')}</span>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <RecipeOwnerInfoModal isOpen={showOwnerInfo} onClose={() => setShowOwnerInfo(false)} owner={owner} language={language} />
        </>
    );
};

export default RecipeCard;
